import { Injectable } from '@angular/core';
import {
  AccessSubscription,
  CreateOrderInput,
  CreateOrderMutation,
  LoadStripePlansQuery,
  LoadStripeProductsQuery,
  LoadStripeSubscriptionsQuery,
  UpdateStripeSubscriptionInput,
  UpdateStripeSubscriptionMutation,
  UsageSubscription
} from '@queries/generated';
import { map } from 'rxjs/operators';

@Injectable({
  providedIn: 'root'
})
export class StripeService {
  constructor(
    private createOrderMutation: CreateOrderMutation,
    private loadStripePlansQuery: LoadStripePlansQuery,
    private loadStripeProductsQuery: LoadStripeProductsQuery,
    private loadStripeSubscriptionsQuery: LoadStripeSubscriptionsQuery,
    private updateStripeSubscriptionMutation: UpdateStripeSubscriptionMutation
  ) {}

  loadPlans() {
    return this.loadStripePlansQuery.watch().valueChanges.pipe(map((resp) => resp.data.stripePlans.all.nodes));
  }

  loadProducts() {
    return this.loadStripeProductsQuery.watch().valueChanges.pipe(map((resp) => resp.data.stripeProducts.all.nodes));
  }

  loadUserSubscriptions() {
    return this.loadStripeSubscriptionsQuery
      .watch()
      .valueChanges.pipe(map((resp) => resp.data.stripeSubscriptions.mine));
  }

  loadUsageSubscriptions() {
    return this.loadUserSubscriptions().pipe(
      map(
        (subscriptions) => subscriptions.filter((subscription) => subscription.usageControlled) as UsageSubscription[]
      )
    );
  }

  loadAccessSubscriptions() {
    return this.loadUserSubscriptions().pipe(
      map(
        (subscriptions) => subscriptions.filter((subscription) => subscription.accessControlled) as AccessSubscription[]
      )
    );
  }

  updateSubscription(input: UpdateStripeSubscriptionInput) {
    return this.updateStripeSubscriptionMutation
      .mutate({ input })
      .pipe(map(({ data }) => data.updateStripeSubscription));
  }

  // service methods

  createOrder(input: CreateOrderInput) {
    return this.createOrderMutation.mutate({ input }).pipe(
      map(({ data }) => data.createOrder),
      map(({ shoppingCart, gatewayInfo, errors, order }) => {
        if (errors?.length) {
          const { 0: error } = errors;
          throw new Error(error.message);
        }
        return { shoppingCart, gatewayInfo, order };
      })
    );
  }
}
